import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { needsOnboarding } from '@/lib/onboarding'
import { useOnboardingStore } from '@/store/onboardingStore'
import { ArrowRight } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import { getEnums } from '@/services/jobService'
import { getProfile, updateProfile, uploadImage, type CompanyProfile } from '@/services/profileService'
import ImageUploader from '@/components/ui/ImageUploader'
import { companySizeLabel } from '@/lib/companySize'
import { useAuthStore } from '@/store/authStore'
import onboardingIllustration from '@/assets/onboarding-illustration.jpg'
import { filterTaxId, isValidTaxId } from '@/lib/inputFilters'

const inputClass =
  'mt-1.5 flex h-12 w-full rounded-none border border-[#E7E5FF] bg-white px-4 text-sm text-[#19172A] transition-colors placeholder:text-[#69738A]/60 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/15'

const labelClass = 'text-sm font-medium text-[#19172A]'

const BENEFITS_COMPANY = [
  'โพสประกาศงานให้ผู้สมัครที่ใช่เห็นได้ทันที',
  'ส่งพนักงานเรียนคอร์สที่เหมาะกับทีมของคุณ',
  'สร้างความน่าเชื่อถือให้บริษัทของคุณ',
]

// ── Provided illustration asset — rendered as-is, square frame ────────────────
function CareerIllustration() {
  return (
    <div className="relative mt-10 w-full">
      <img
        src={onboardingIllustration}
        alt="ผู้ใช้งานกำลังทำงานกับโน้ตบุ๊ก"
        className="relative w-full"
      />
    </div>
  )
}

// ── Shared shell: navbar + balanced 2-column layout ────────────────────────────
function OnboardingShell({
  eyebrow, heading, description, benefits, illustration, children,
}: {
  eyebrow: string; heading: ReactNode; description: string
  benefits: string[]
  illustration?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="min-h-screen bg-[#FAFAFF]">
      <Navbar />

      <div className="mx-auto flex max-w-[1320px] flex-col gap-16 px-4 py-16 md:px-10 lg:flex-row lg:items-start lg:gap-[64px] lg:py-20">

        {/* ── LEFT — editorial welcome + illustration (42%) ── */}
        <div className="lg:w-[42%] lg:shrink-0">
          <p className="text-sm font-medium text-accent">{eyebrow}</p>
          <h1 className="mt-3 font-display text-[34px] font-bold leading-[1.22] tracking-[-0.01em] text-[#19172A] sm:text-[38px]">
            {heading}
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-[#69738A]">
            {description}
          </p>

          <ul className="mt-6 space-y-2.5">
            {benefits.map((text) => (
              <li key={text} className="flex items-baseline gap-3 text-[14px] text-[#19172A]">
                <span className="h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full bg-accent" />
                {text}
              </li>
            ))}
          </ul>

          {illustration}
        </div>

        {/* ── RIGHT — form card (58%) ── */}
        <div className="lg:w-[58%] lg:flex-1">
          {children}
        </div>
      </div>
    </div>
  )
}

function FormCard({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <div className="rounded-none border border-[#E8E5F4] bg-white p-7 shadow-[0_2px_24px_rgba(108,99,255,0.06)] sm:p-10">
      <h2 className="text-xl font-bold text-[#19172A] sm:text-[22px]">{title}</h2>
      <p className="mt-1.5 text-sm text-[#69738A]">{description}</p>
      {children}
    </div>
  )
}

function InfoBox({ text }: { text: string }) {
  return (
    <div className="rounded-none bg-[#F5F3FF] px-4 py-3.5">
      <p className="text-[13px] leading-relaxed text-[#69738A]">{text}</p>
    </div>
  )
}

// ── Company variant — mandatory fields, no skip ────────────────────────────────
function CompanyOnboarding({ redirectTo }: { redirectTo: string }) {
  const navigate = useNavigate()
  const [companyName, setCompanyName] = useState('')
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)   // โลโก้ที่เลือกไว้ รออัปโหลดตอนกดบันทึก
  const [industry, setIndustry] = useState('')
  const [companySize, setCompanySize] = useState('')
  const [location, setLocation] = useState('')
  const [taxId, setTaxId] = useState('')
  const [website, setWebsite] = useState('')
  const [description, setDescription] = useState('')
  const [sizeOptions, setSizeOptions] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    getEnums().then(data => setSizeOptions(data.CompanySize || [])).catch(() => setSizeOptions([]))
    // ดึงชื่อบริษัทที่กรอกตอนสมัครมาแสดง (แก้ไขได้)
    getProfile().then(p => {
      const c = p as CompanyProfile
      setCompanyName(c.company_name ?? '')
      setLogoUrl(c.logo_url ?? null)
    }).catch(() => {})
  }, [])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!companyName.trim() || !industry.trim() || !companySize || !location.trim() || !taxId.trim()) {
      setError('กรุณากรอกข้อมูลที่จำเป็นให้ครบก่อนดำเนินการต่อ')
      return
    }
    if (!isValidTaxId(taxId.trim())) {
      setError('เลขประจำตัวผู้เสียภาษีต้องเป็นตัวเลข 13 หลัก')
      return
    }
    setError('')
    setSaving(true)
    try {
      const logo = logoFile ? await uploadImage(logoFile) : logoUrl   // อัปโหลดโลโก้ที่เลือกไว้ (ล้มเหลว = ไม่บันทึกอะไรเลย)
      await updateProfile({
        company_name: companyName.trim(),
        logo_url: logo,
        industry: industry.trim(),
        company_size: companySize,
        location: location.trim(),
        tax_id: taxId.trim(),
        website: website.trim() || null,
        description: description.trim() || null,
      })
      const cid = useAuthStore.getState().customerId
      if (cid != null) useOnboardingStore.getState().set(cid, 'complete')
      navigate(redirectTo, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'บันทึกโปรไฟล์ไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <OnboardingShell
      eyebrow="ยินดีต้อนรับสู่ JosCoWork"
      heading={<>มาเติมข้อมูล<br />บริษัทของคุณ</>}
      description="ต้องกรอกข้อมูลให้ครบก่อน จึงจะสามารถโพสประกาศงานและใช้งานฟีเจอร์สำหรับ Employer ได้"
      benefits={BENEFITS_COMPANY}
      illustration={<CareerIllustration />}
    >
      <FormCard title="ข้อมูลบริษัทของคุณ" description="กรุณากรอกข้อมูลให้ครบถ้วน — จำเป็นสำหรับการยืนยันตัวตนบริษัท">
        <form onSubmit={handleSave} className="mt-7 space-y-5">
          <ImageUploader label="โลโก้บริษัท" value={logoFile ?? logoUrl} fallback={companyName[0]?.toUpperCase() || '·'}
            onChange={(x) => { if (x instanceof File) setLogoFile(x); else { setLogoFile(null); setLogoUrl(x || null) } }} />

          <div>
            <label htmlFor="ob-name" className={labelClass}>
              ชื่อบริษัท <span className="text-[#69738A]/60">(จำเป็น)</span>
            </label>
            <input id="ob-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
              className={inputClass} placeholder="ชื่อบริษัท" required />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="ob-industry" className={labelClass}>
                อุตสาหกรรม <span className="text-[#69738A]/60">(จำเป็น)</span>
              </label>
              <input id="ob-industry" value={industry} onChange={(e) => setIndustry(e.target.value)}
                className={inputClass} placeholder="เช่น เทคโนโลยี, การเงิน" required />
            </div>
            <div>
              <label htmlFor="ob-size" className={labelClass}>
                ขนาดบริษัท <span className="text-[#69738A]/60">(จำเป็น)</span>
              </label>
              <select id="ob-size" value={companySize} onChange={(e) => setCompanySize(e.target.value)}
                className={inputClass} required>
                <option value="">เลือกขนาดบริษัท...</option>
                {sizeOptions.map(v => <option key={v} value={v}>{companySizeLabel(v)}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="ob-location" className={labelClass}>
              ที่ตั้งบริษัท <span className="text-[#69738A]/60">(จำเป็น)</span>
            </label>
            <input id="ob-location" value={location} onChange={(e) => setLocation(e.target.value)}
              className={inputClass} placeholder="เช่น กรุงเทพฯ" required />
          </div>

          <div>
            <label htmlFor="ob-taxid" className={labelClass}>
              เลขประจำตัวผู้เสียภาษี <span className="text-[#69738A]/60">(จำเป็น)</span>
            </label>
            <input id="ob-taxid" value={taxId} onChange={(e) => setTaxId(filterTaxId(e.target.value))} inputMode="numeric" maxLength={13}
              className={inputClass} placeholder="เลข 13 หลัก" required />
          </div>

          <div>
            <label htmlFor="ob-website" className={labelClass}>เว็บไซต์บริษัท</label>
            <input id="ob-website" value={website} onChange={(e) => setWebsite(e.target.value)}
              className={inputClass} placeholder="https://..." />
          </div>

          <div>
            <label htmlFor="ob-desc" className={labelClass}>เกี่ยวกับบริษัท</label>
            <textarea id="ob-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              rows={4} className={`${inputClass} h-auto resize-none py-3`}
              placeholder="แนะนำบริษัทของคุณสั้น ๆ..." />
          </div>

          <InfoBox text="ข้อมูลนี้จำเป็นสำหรับการยืนยันตัวตนบริษัท ทีมงานจะตรวจสอบก่อนอนุมัติให้โพสประกาศงานได้" />

          {error && <p className="text-sm text-rose-500">{error}</p>}

          <div className="flex justify-end border-t border-[#F0EEFB] pt-6">
            <button type="submit" disabled={saving}
              className="flex h-11 items-center justify-center gap-2 rounded-none bg-accent px-6 text-sm font-semibold text-white transition-colors hover:bg-accent2 disabled:opacity-50">
              {saving ? 'กำลังบันทึก...' : 'บันทึกและเริ่มใช้งาน'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </FormCard>
    </OnboardingShell>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function OnboardingPage() {
  const location = useLocation()
  const redirectTo = (location.state as { from?: string })?.from || '/'
  const customerId = useAuthStore((s) => s.customerId)
  const customerType = useAuthStore((s) => s.customerType)

  // เฉพาะบริษัทที่ข้อมูลยังไม่ครบเท่านั้นที่เห็นหน้านี้ — บุคคลทั่วไป/บริษัทที่กรอกครบแล้วจะถูกส่งกลับทันที
  const [check, setCheck] = useState<'loading' | 'needed' | 'done'>('loading')
  useEffect(() => {
    if (customerType !== 'COMPANY') return
    getProfile()
      .then((p) => setCheck(needsOnboarding(p) ? 'needed' : 'done'))
      .catch(() => setCheck('needed'))
  }, [customerId, customerType])

  if (customerType !== 'COMPANY') return <Navigate to="/" replace />
  if (check === 'loading') return null
  if (check === 'done') return <Navigate to={redirectTo} replace />
  return <CompanyOnboarding redirectTo={redirectTo} />
}
